import React, { useMemo } from 'react';
import { MarketDataTable } from './MarketDataTable';
import { MarketBarChart } from './MarketBarChart';
import { MarketHorizontalBarChart } from './MarketHorizontalBarChart';
import {
  buildRatingPerformance, buildValueBadgePerformance, buildJockeyPerformance,
  buildTrainerPerformance,
} from '../../utils/marketAnalytics';
import {
  formatPct, formatMultiple, formatMoney, formatRating, formatNumber, SAMPLE_SIZE_COPY,
} from '../../utils/marketFormatters';

const GROUP_COLUMNS = (firstLabel) => [
  { key: 'groupKey', label: firstLabel, sortable: true },
  { key: 'runnerCount', label: 'Runners', align: 'right', sortable: true, render: r => formatNumber(r.runnerCount) },
  { key: 'wins', label: 'Wins', align: 'right', sortable: true, render: r => formatNumber(r.wins) },
  { key: 'winPct', label: 'Win %', align: 'right', sortable: true, render: r => formatPct(r.winPct) },
  { key: 'top5Pct', label: 'Top-5 %', align: 'right', sortable: true, render: r => formatPct(r.top5Pct) },
  { key: 'roi', label: 'Avg ROI', align: 'right', sortable: true, render: r => formatPct(r.roi) },
  { key: 'returnMultiple', label: 'Return Multiple', align: 'right', sortable: true, render: r => formatMultiple(r.returnMultiple) },
];

const VALUE_COLUMNS = [
  { key: 'groupKey', label: 'Value Badge', sortable: true },
  { key: 'runnerCount', label: 'Runners', align: 'right', sortable: true, render: r => formatNumber(r.runnerCount) },
  { key: 'wins', label: 'Wins', align: 'right', sortable: true, render: r => formatNumber(r.wins) },
  { key: 'top5Pct', label: 'Top-5 %', align: 'right', sortable: true, render: r => formatPct(r.top5Pct) },
  { key: 'roi', label: 'Avg ROI', align: 'right', sortable: true, render: r => formatPct(r.roi) },
  { key: 'avgOwnershipPct', label: 'Avg Ownership %', align: 'right', sortable: true, render: r => formatPct(r.avgOwnershipPct) },
];

function NameCell({ name, sampleOk }) {
  return (
    <span className="mk-person-cell">
      {name}
      {!sampleOk && <span className="mk-pill mk-pill--muted mk-pill--tiny">small sample</span>}
    </span>
  );
}

const personColumns = (label) => [
  { key: 'name', label, sortable: true, render: r => <NameCell name={r.name} sampleOk={r.sampleOk} /> },
  { key: 'settledRunnerCount', label: 'Settled Runners', align: 'right', sortable: true, render: r => formatNumber(r.settledRunnerCount) },
  { key: 'wins', label: 'Wins', align: 'right', sortable: true, render: r => formatNumber(r.wins) },
  { key: 'top5Pct', label: 'Top-5 %', align: 'right', sortable: true, render: r => formatPct(r.top5Pct) },
  { key: 'avgRunnerRating', label: 'Avg Rating', align: 'right', sortable: true, render: r => formatRating(r.avgRunnerRating) },
  { key: 'roi', label: 'ROI', align: 'right', sortable: true, render: r => formatPct(r.roi) },
  { key: 'totalEarnings', label: 'Virtual Earnings', align: 'right', sortable: true, render: r => formatMoney(r.totalEarnings) },
];

function toRatingChartData(groups) {
  return (groups || []).map(g => ({ label: g.groupKey, value: g.winPct, sampleSize: g.settledRunnerCount }));
}

function toPersonChartData(list) {
  return (list || [])
    .filter(p => p.settledRunnerCount > 0)
    .slice(0, 10)
    .map(p => ({ label: p.name, value: p.winPct, sampleSize: p.settledRunnerCount, sampleOk: p.sampleOk }));
}

export const MarketPerformanceTab = ({ rows, loading }) => {
  const ratingPerf = useMemo(() => buildRatingPerformance(rows), [rows]);
  const valuePerf = useMemo(() => buildValueBadgePerformance(rows), [rows]);
  const jockeyPerf = useMemo(() => buildJockeyPerformance(rows), [rows]);
  const trainerPerf = useMemo(() => buildTrainerPerformance(rows), [rows]);

  return (
    <div className="mk-tab-content">
      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Rating Band Analytics</h2>
          <p className="mk-section-subtitle">Does a higher public rating actually translate into results? Settled races only.</p>
        </div>
        <MarketBarChart data={toRatingChartData(ratingPerf)} valueFormatter={formatPct} sampleLabel="settled runners" />
        <MarketDataTable
          columns={GROUP_COLUMNS('Rating Band')}
          rows={ratingPerf}
          getRowKey={r => r.groupKey}
          loading={loading}
          emptyTitle="No rating performance data yet"
          emptyMessage="Market data will appear once more races are settled."
        />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Value Badge Analytics</h2>
          <p className="mk-section-subtitle">Is the value model finding genuine bargains? Settled races only.</p>
        </div>
        <MarketBarChart data={toRatingChartData(valuePerf).map(d => ({ ...d }))} valueFormatter={formatPct} sampleLabel="settled runners" />
        <MarketDataTable
          columns={VALUE_COLUMNS}
          rows={valuePerf}
          getRowKey={r => r.groupKey}
          loading={loading}
          emptyTitle="No value badge performance data yet"
          emptyMessage="Market data will appear once more races are settled."
        />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Jockey Performance</h2>
          <p className="mk-section-subtitle">{SAMPLE_SIZE_COPY}</p>
        </div>
        <MarketHorizontalBarChart data={toPersonChartData(jockeyPerf)} valueFormatter={formatPct} />
        <MarketDataTable
          columns={personColumns('Jockey')}
          rows={jockeyPerf}
          getRowKey={r => r.name}
          loading={loading}
          initialSortKey="winPct"
          emptyIcon="🏇"
          emptyTitle="No jockey data yet"
          emptyMessage="More settled races are needed before jockey rankings become reliable."
        />
      </section>

      <section className="mk-section">
        <div className="mk-section-header">
          <h2 className="mk-section-title">Trainer Performance</h2>
          <p className="mk-section-subtitle">{SAMPLE_SIZE_COPY}</p>
        </div>
        <MarketHorizontalBarChart data={toPersonChartData(trainerPerf)} valueFormatter={formatPct} />
        <MarketDataTable
          columns={personColumns('Trainer')}
          rows={trainerPerf}
          getRowKey={r => r.name}
          loading={loading}
          initialSortKey="winPct"
          emptyIcon="🎽"
          emptyTitle="No trainer data yet"
          emptyMessage="More settled races are needed before trainer rankings become reliable."
        />
      </section>
    </div>
  );
};

export default MarketPerformanceTab;
